import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import TextareaAutosize from 'react-textarea-autosize';
import {
  likelySentenceIndex,
  ReaderSelection,
  splitSentences,
  spliceText,
} from '../../lib/reader/selection';
import { useSuggestWording } from '../../hooks/useTranslationProjects';

interface Props {
  selection: ReaderSelection;
  segmentId: string;
  // Source-language text of the selected segment — shown as a steppable
  // sentence list so the reader can check the original while rewording.
  sourceText?: string | null;
  sourceLanguage: string;
  targetLanguage: string;
  saving: boolean;
  // Receives the full new target text (the chosen phrase spliced back into the
  // segment body).
  onApply: (newTargetText: string) => void;
  // Shares the selected phrase as a quote (native share sheet / clipboard).
  onShare?: () => void;
  onClose: () => void;
}

/**
 * Bottom sheet for rewording a selected phrase. The reader can edit the phrase
 * by hand or ask the AI for drop-in alternatives, while stepping through the
 * original sentence-by-sentence for reference. Applying splices the chosen
 * wording back into the full segment and saves it. Mirrors TransliFlow's reader.
 */
export default function WordingSheet({
  selection,
  segmentId,
  sourceText,
  sourceLanguage,
  targetLanguage,
  saving,
  onApply,
  onShare,
  onClose,
}: Props) {
  const { t } = useTranslation();
  const suggest = useSuggestWording();
  const [draft, setDraft] = useState(selection.text);
  const [options, setOptions] = useState<string[]>([]);
  const [copied, setCopied] = useState(false);

  // The original split into sentences, so the reader can step through it; the
  // proportional match is only a starting guess that prev/next can correct.
  const sentences = useMemo(() => splitSentences(sourceText ?? ''), [sourceText]);
  const [sentIdx, setSentIdx] = useState(0);
  const activeRef = useRef<HTMLElement | null>(null);

  // On a new selection: reset the draft and jump to the likely source sentence.
  useEffect(() => {
    setDraft(selection.text);
    setOptions([]);
    setCopied(false);
    suggest.reset();
    setSentIdx(
      likelySentenceIndex(selection.base, selection.start, selection.end, sentences),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selection, sourceText]);

  // Keep the focused sentence in view within its scrollable box only.
  useEffect(() => {
    activeRef.current?.scrollIntoView({ block: 'nearest' });
  }, [sentIdx]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const askAi = async () => {
    const list = await suggest.mutateAsync({
      segmentId,
      selectedText: selection.text,
      precedingText: selection.base.slice(0, selection.start),
      followingText: selection.base.slice(selection.end),
    });
    setOptions(list);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(selection.text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable (insecure context / denied) */
    }
  };

  const apply = () => {
    const phrase = draft.trim();
    if (!phrase) return;
    onApply(spliceText(selection.base, selection.start, selection.end, phrase));
  };

  const unchanged = draft.trim() === selection.text.trim();

  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end" role="dialog" aria-modal="true">
      <button aria-label="Close" onClick={onClose} className="absolute inset-0 bg-slate-900/30" />
      <div className="relative max-h-[80vh] overflow-y-auto rounded-t-2xl bg-white px-5 pb-6 pt-3 shadow-2xl dark:bg-gray-800">
        <div className="mx-auto mb-4 h-1 w-10 rounded-full bg-slate-300" />

        <div className="mb-1 flex items-center justify-between">
          <span className="text-[11px] font-medium uppercase tracking-wide text-slate-400">
            {t('translationReader.alternativeWording')}
          </span>
          <div className="-mr-1 flex items-center gap-1">
            <button
              onClick={copy}
              title={t('translationReader.copyQuote')}
              className="rounded-md px-2 py-1 text-sm font-medium text-slate-500 hover:bg-slate-100 dark:hover:bg-gray-700"
            >
              {copied ? `✓ ${t('common.copied')}` : `⧉ ${t('common.copy')}`}
            </button>
            {onShare && (
              <button
                onClick={onShare}
                title={t('translationReader.shareQuote')}
                className="rounded-md px-2 py-1 text-sm font-medium text-slate-500 hover:bg-slate-100 dark:hover:bg-gray-700"
              >
                ↗ {t('translationReader.share')}
              </button>
            )}
          </div>
        </div>

        {sentences.length > 0 && (
          <div className="mb-4 rounded-lg bg-slate-50 px-3 py-2 dark:bg-gray-900">
            <div className="mb-1 flex items-center justify-between">
              <span className="text-[10px] font-medium uppercase tracking-wide text-slate-400">
                {t('translationReader.likelyOriginal', { lang: sourceLanguage })}
              </span>
              {sentences.length > 1 && (
                <span className="flex items-center gap-1 text-slate-500">
                  <button
                    onClick={() => setSentIdx((i) => Math.max(0, i - 1))}
                    disabled={sentIdx === 0}
                    title={t('translationReader.prevSentence')}
                    className="rounded px-1.5 py-0.5 hover:bg-slate-200 disabled:opacity-30 dark:hover:bg-gray-700"
                  >
                    ‹
                  </button>
                  <span className="text-[10px] tabular-nums text-slate-400">
                    {sentIdx + 1}/{sentences.length}
                  </span>
                  <button
                    onClick={() => setSentIdx((i) => Math.min(sentences.length - 1, i + 1))}
                    disabled={sentIdx === sentences.length - 1}
                    title={t('translationReader.nextSentence')}
                    className="rounded px-1.5 py-0.5 hover:bg-slate-200 disabled:opacity-30 dark:hover:bg-gray-700"
                  >
                    ›
                  </button>
                </span>
              )}
            </div>
            <div className="max-h-20 overflow-y-auto font-serif text-sm leading-snug text-slate-400">
              {sentences.map((s, i) => (
                <Fragment key={i}>
                  {i > 0 ? ' ' : ''}
                  {i === sentIdx ? (
                    <mark ref={activeRef} className="rounded bg-amber-100/70 text-slate-700">
                      {s.text}
                    </mark>
                  ) : (
                    s.text
                  )}
                </Fragment>
              ))}
            </div>
          </div>
        )}

        <label className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-slate-400">
          {t('translationReader.editYourself', { lang: targetLanguage })}
        </label>
        <TextareaAutosize
          className="w-full resize-none rounded-lg border border-slate-300 p-2 font-serif text-[1.05rem] dark:border-gray-600 dark:bg-gray-900"
          minRows={1}
          maxRows={8}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          autoFocus
        />

        <div className="mt-3 flex items-center gap-2">
          <button
            onClick={apply}
            disabled={saving || unchanged || !draft.trim()}
            className="rounded bg-slate-900 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50"
          >
            {saving ? t('common.applying') : t('common.apply')}
          </button>
          <button
            onClick={askAi}
            disabled={suggest.isPending}
            className="rounded border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-50 dark:border-gray-600 dark:text-slate-300 dark:hover:bg-gray-700"
          >
            {suggest.isPending ? t('translationReader.askingAi') : `✨ ${t('translationReader.askAi')}`}
          </button>
          <button onClick={onClose} className="ml-auto px-2 py-1.5 text-sm text-slate-500">
            {t('common.cancel')}
          </button>
        </div>

        {suggest.isError && (
          <p className="mt-3 text-sm text-red-600">
            {suggest.error instanceof Error && suggest.error.message
              ? suggest.error.message
              : t('translationReader.suggestError')}
          </p>
        )}

        {options.length > 0 && (
          <div className="mt-4 space-y-2">
            <div className="text-[11px] font-medium uppercase tracking-wide text-slate-400">
              {t('translationReader.tapSuggestion')}
            </div>
            {options.map((opt, i) => (
              <button
                key={i}
                onClick={() => setDraft(opt)}
                className={`block w-full rounded-lg border p-3 text-left font-serif text-[1.05rem] leading-relaxed hover:bg-slate-50 dark:hover:bg-gray-700 ${
                  draft === opt
                    ? 'border-slate-900 bg-slate-50 dark:border-slate-400 dark:bg-gray-700'
                    : 'border-slate-200 dark:border-gray-600'
                }`}
              >
                {opt}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
